import { CurrencyEnum, PlanDetailsModel } from './plan.interface';

export enum UserAuthTypeEnum {
    emailPassword = 'email/password',
    google = 'google',
}

export enum UserRoleEnum {
    user = 'user',
    student = 'student',
    teacher = 'teacher',
    superAdmin = 'super-admin',
    admin = 'admin',
    customerService = 'customer-service',
}

export enum UserLocalStorageKeysEnum {
    CURRENT_USER = 'current_user',
    TOKEN = 'token',
    BOOK_ID = 'bookId',
    EDIT_MODE = 'editMode',
    STORY_STEP = 'storyStep',
    ACTIVE_CLASSROOM_ID = 'activeClassroomId',
    ACTIVE_CLASSROOM_PROJECT_ID = 'activeClassroomProjectId',
    TEACHER_DRAFT_FORM = 'teacherDraftForm',
}

export enum UserStatusEnum {
    ACTIVE = 'active',
    INACTIVE = 'inactive'
}

export enum UserSettingsEnum {
    MARKETING_EMAILS = 'USER_ALLOW_MARKETING_EMAILS',
    PROMOTIONAL_EMAILS = 'USER_ALLOW_PROMOTIONAL_EMAILS'
}

export enum UserTypeEnum {
    StandardUser = 'standard',
    Teacher = 'teacher',
}

export enum TeacherOnboardingStatusEnum {
    BookOnboarding = 'book_onboarding',
    TeacherInfo = 'teacher_info',
    Complete = 'complete',
}

export const UserTypeLabelEnum = {
    [UserTypeEnum.StandardUser]: 'Standard User',
    [UserTypeEnum.Teacher]: 'Teacher',
}

export enum UserTypeActivity {
    SIGN_UP = 'SIGN_UP',
    NEW_BOOK = 'NEW_BOOK',
    CREATE_MAIN_CHARACTER = 'CREATE_MAIN_CHARACTER',
    GENERATE_FULL_BOOK_DESCRIPTION = 'GENERATE_FULL_BOOK_DESCRIPTION',
    GENERATE_ALL_BOOK_IMAGES = 'GENERATE_ALL_BOOK_IMAGES',
}

export const UserTypeActivityTitle = {
    [UserTypeActivity.SIGN_UP]: 'User signed up',
    [UserTypeActivity.NEW_BOOK]: 'New book created (1 step)',
    [UserTypeActivity.CREATE_MAIN_CHARACTER]: 'Creation Main character (2 step)',
    [UserTypeActivity.GENERATE_FULL_BOOK_DESCRIPTION]: 'Full book description generation (3 step)',
    [UserTypeActivity.GENERATE_ALL_BOOK_IMAGES]: 'All book images generation (4 step)',
}

export interface UserShippingAddress {
    shippingName: string;
    recipientName: string;
    shippingAddress1: string;
    shippingAddress2: string;
    shippingCity: string;
    shippingState: string;
    shippingZip: string;
    shippingCountry: string;
}

export interface UserModel {
    id?: string;
    settings?: UserSettingsModel;
    authType?: UserAuthTypeEnum;
    creditBalance?: number;
    email?: string;
    firebaseId?: string;
    firstName?: string;
    isAdult?: string;
    lastName?: string;
    role?: UserRoleEnum;
    promoCode?: string | null;
    status?: string;
    verified?: string;
    planRef?: any;
    plan?: PlanDetailsModel | null;
    planExpireAt?: Date | null;
    disablePlanCancellation?: boolean;

    subscribedOnPlanAt?: Date | null;
    freePhysicalBookCreated?: number;
    freePhysicalBookQuantity?: number;
    stripeSubscriptionId?: string;
    customerName?: string;
    shippingAddress1?: string;
    shippingAddress2?: string;
    shippingState?: string;
    shippingCountry?: string;
    shippingCity?: string;
    shippingZip?: string;
    billingCardHolderName?: string;
    billingAddress1?: string;
    billingAddress2?: string;
    billingCity?: string;
    billingPostalCode?: string;
    coupon?: string;
    billingState?: string;
    showTrainingPopup?: { [screenNumber: string]: boolean };
    booksCreateLimit?: number;
    booksCreatedCounter?: number;
    timestamp?: Date;
    lastSessionAt?: Date;
    lastSessionActivity?: UserTypeActivity;
    uid?: string;
    auth?: boolean;
    acceptedTermsAndConditions?: boolean;
    closeTermsAndConditions?: boolean;

    isTrialFreeAccess?: boolean;
    librarySelectMode?: boolean;
    isTeacherMode?: boolean;
    saveDefaultShippingAddress?: boolean;
    shippingAddresses?: Array<UserShippingAddress> | null;

    bookFlyersGenerationProgress?: number | null,
    bookFlyersPdfPath?: string | null,
    bookFlyersGenerationAt?: Date | string | null,

    preSessionFlyersGenerationProgress?: number | null;
    preSessionFlyersPdfPath?: string | null;
    preSessionFlyersGenerationAt?: Date | string | null;

    // Currency detection fields
    preferredCurrency?: CurrencyEnum;
    detectedCountry?: string;
    detectedCurrency?: CurrencyEnum;
    countryDetectionAt?: Date;

    // Student-specific fields
    classroomId?: string;
    teacherEmail?: string;
    generatedEmail?: boolean;
    studentName?: string;
    loginName?: string;
    encryptedPassword?: string;
    assignedProjects?: string[];
    submissions?: {
        projectId: string;
        submittedAt: Date;
        status: string;
        content?: any;
    }[];

    lead?: {
        projectId?: string;
        leadId?: string;
        email?: string;
        createdAt?: Date;
    };

    // Teacher onboarding status tracking
    teacherOnboardingStatus?: TeacherOnboardingStatusEnum;
}

export interface UserShortModel {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: UserRoleEnum;
    status: string;
    settings: UserSettingsModel;
    promoCode?: string | null;
}

export interface UserSettingsModel {
    [key: string]: boolean | undefined;
    USER_SETTING_MARKETING_EMAILS: boolean;
    USER_SETTING_PROMOTIONAL_EMAILS: boolean;
    USER_SETTING_TEACHER_OR_GROUP?: boolean;
}

export const userSettings: UserSettingsModel = {
    USER_SETTING_MARKETING_EMAILS: false,
    USER_SETTING_PROMOTIONAL_EMAILS: false,
    USER_SETTING_TEACHER_OR_GROUP: false
};

export interface UserPlanModel {
    cost?: number;
    costHistory?: UserPlanHistoryItemModel[];
    stripe_plan_id?: string;
    timestamp?: Date;
}

export interface UserPlanHistoryItemModel {
    cost?: number;
    stripe_plan_id?: string;
    timestamp?: Date;
}